import React, { useContext, useEffect, useState } from 'react'
import { CollectionThemeProps, InviteType, QuizInviteType } from '../../../../lib/types'
import { format, parseJSON } from 'date-fns'
import Box from '@mui/material/Box'
import Step2, { UploadState } from './steps/Step2'
import Step1 from './steps/Step1'
import Step3 from './steps/Step3'
import api from '../../../../lib/api'
import { SimpleDialogContext } from '../../../../components/SimpleDialog'
import { useCacheStorage } from '../../../../components/FileStorage/CacheStorage'

const UPLOAD_MAX_ATTEMPTS = 3
const UPLOAD_RETRY_DELAYS_MS = [2000, 5000, 10000]

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

interface iQuestionnaireProps {
  invite: InviteType
  id: string
  token: string
  recorderName: string
  recorderEmail: string
  recorderJobTitle: string
  recorderFunctionalArea: string
  recorderCountry: string
  quiz: QuizInviteType
  collectionTheme?: CollectionThemeProps
}

const Questionnaire: React.FC<iQuestionnaireProps> = ({
  quiz,
  invite,
  token,
  id,
  recorderName,
  recorderEmail,
  recorderJobTitle,
  recorderFunctionalArea,
  collectionTheme,
  recorderCountry
}) => {
  const questions = (quiz.questions ?? []).sort((a, b) => a.number - b.number)
  const [keepAudioOnly, setKeepAudioOnly] = useState<boolean>(false)

  const [activeStep, setActiveStep] = useState<string>(() => {
    const lastItem = localStorage.getItem(`io.rakonto.questionnaire.${quiz.id}.lastQuestion`)
    return lastItem || 'start'
  })

  const [user, setUser] = useState<{
    name: string
    email: string
    jobTitle: string
    functionalArea: string
    country: string
    audioOnly: boolean
  }>(() => {
    const hasLastItem = localStorage.getItem(`io.rakonto.questionnaire.${quiz.id}.lastQuestion`)
    const email = localStorage.getItem(`io.rakonto.questionnaire.${quiz.id}.guestUser.email`)
    const name = localStorage.getItem(`io.rakonto.questionnaire.${quiz.id}.guestUser.name`)
    const jobTitle = recorderJobTitle
    const functionalArea = recorderFunctionalArea
    const country = recorderCountry
    const audioOnly = keepAudioOnly

    if (email && name) {
      console.log('Will continue form start point of last questionnaire')
      if (!hasLastItem) setActiveStep(questions[0].id)
      return { name, email, jobTitle, functionalArea, country, audioOnly }
    }

    return {
      name: recorderName,
      email: recorderEmail,
      jobTitle: recorderJobTitle,
      functionalArea: recorderFunctionalArea,
      country: recorderCountry,
      audioOnly: keepAudioOnly
    }
  })
  const [progress, setProgress] = useState(0)
  const [uploadState, setUploadState] = useState<UploadState>('idle')
  const [uploadAttempt, setUploadAttempt] = useState(0)
  const [uploadError, setUploadError] = useState<string>('')
  const [restoredFile, setRestoredFile] = useState<File | null>(null)
  const [finishFailed, setFinishFailed] = useState(false)

  const { actions: simpleDialogActions } = useContext(SimpleDialogContext)
  const cacheStorage = useCacheStorage()

  const cacheKeyFor = (questionId: string) => `questionnaire.${quiz.id}.${questionId}`

  // Warn before closing the tab while an upload is still in flight.
  useEffect(() => {
    if (uploadState !== 'uploading' && uploadState !== 'retrying') return
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [uploadState])

  // Restore an unsent recording saved locally for the current question, if any.
  useEffect(() => {
    let cancelled = false
    setRestoredFile(null)
    const isQuestionStep = questions.some(question => question.id === activeStep)
    if (!isQuestionStep) return
    const restore = async () => {
      const cached = await cacheStorage.getFile(cacheKeyFor(activeStep))
      if (cancelled || !cached) return
      setRestoredFile(cached)
      const title = 'Unsent recording found'
      const message =
        'We found a recording for this question that was not uploaded yet. ' +
        'It has been restored - press Submit to upload it, or remove it to record again.'
      if (collectionTheme) {
        simpleDialogActions.openWithStyles(title, message, collectionTheme, { cancelText: 'Ok', showOk: false })
      } else {
        simpleDialogActions.open(title, message, { cancelText: 'Ok', showOk: false })
      }
    }
    restore()
    return () => {
      cancelled = true
    }
  }, [activeStep])

  const handleSendEmailQuestionSubmit = async (i: number) => {
    let question = questions[i]
    if (!question) {
      question = questions[i - 1]
    }
    console.log('HandleSendEmailQuestionSubmit: ' + question.id)
    const record = {
      email: user.email,
      name: user.name,
      jobTitle: user.jobTitle,
      functionalArea: user.functionalArea
    }

    console.log('Question ID: ' + question.id)
    console.log('Question Title: ' + question.title)

    console.log('Email: ' + record.email)
    console.log('Name: ' + record.name)
    console.log('Job: ' + record.jobTitle)
    console.log('Functional Area: ' + record.functionalArea)

    const getQueryParam = (param: string): string => {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get(param) || ''
    }

    if (!record.jobTitle) {
      record.jobTitle = '_'
    }
    if (!record.functionalArea) {
      record.functionalArea = '_'
    }
    console.log(
      'Handle Submit: ' + record.name + ', ' + record.email + ', ' + record.jobTitle + ', ' + record.functionalArea
    )

    try {
      console.log('Send email: ' + invite!.id + ', ' + record.email)

      await api.sendQuestionEmail(question.id, record, token)
    } catch (e) {
      console.log('Error to send Email: ' + e)
      console.error(e)
    }
  }

  const handleSendEmailCompleteQuestionnaire = async () => {
    const record = {
      email: user.email,
      name: user.name,
      jobTitle: user.jobTitle,
      functionalArea: user.functionalArea
    }

    console.log('Invite ID: ' + invite.id)
    console.log('Invite Title: ' + invite.title)
    console.log('CTA: ' + invite.callToAction)
    console.log('CTA Button: ' + invite.callToActionButtonLabel)
    console.log('CTA Instructions: ' + invite.callToActionInstructions)

    console.log('Email: ' + record.email)
    console.log('Name: ' + record.name)
    console.log('Job: ' + record.jobTitle)
    console.log('Functional Area: ' + record.functionalArea)
    console.log('Questionnnaire ID: ' + invite.questionnaireId)

    if (!record.jobTitle) {
      record.jobTitle = '_'
    }
    if (!record.functionalArea) {
      record.functionalArea = '_'
    }
    console.log(
      'Handle Submit: ' + record.name + ', ' + record.email + ', ' + record.jobTitle + ', ' + record.functionalArea
    )

    // Retry the finish call - it is what marks the questionnaire as completed in the database.
    let finishSucceeded = false
    for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt++) {
      try {
        console.log('Finishing Questionnaire: ' + invite!.id + ', ' + record.email + ' (attempt ' + attempt + ')')
        await api.finishQuestionnaire(
          invite.questionnaireId,
          record.email,
          record.jobTitle,
          record.functionalArea,
          token
        )
        finishSucceeded = true
        break
      } catch (e) {
        console.error('Error to Finish Questionnaire (attempt ' + attempt + '): ' + e)
        if (attempt < UPLOAD_MAX_ATTEMPTS) {
          await sleep(UPLOAD_RETRY_DELAYS_MS[attempt - 1])
        }
      }
    }
    setFinishFailed(!finishSucceeded)

    try {
      console.log('Send email: ' + invite!.id + ', ' + record.email)

      await api.sendQuestionnaireCompletionConfirmationEmail(invite!.id, record, '', true, token)
    } catch (e) {
      console.log('Error to send Email: ' + e)
      console.error(e)
    }
  }
  const setNextStep = (i: number) => {
    if (questions[questions.length - 1].id === activeStep) {
      handleSendEmailCompleteQuestionnaire()
        .then(() => setActiveStep('final'))
        .catch(() => setActiveStep('final'))
    } else {
      setActiveStep(questions[i + 1].id)
      localStorage.setItem(`io.rakonto.questionnaire.${quiz.id}.lastQuestion`, `${questions[i + 1].id}`)
    }
    setProgress(0)
    setUploadState('idle')
    setUploadAttempt(0)
    setUploadError('')
  }

  const reportUploadFailure = async (questionId: string, attempts: number, errorMessage: string, fileSize: number) => {
    try {
      const question = questions.find(q => q.id === questionId)
      await api.sendQuestionnaireUploadFailure(token, {
        questionnaireId: question?.questionnaireId || invite.questionnaireId,
        questionId,
        name: user.name,
        email: user.email,
        jobTitle: user.jobTitle || '_',
        functionalArea: user.functionalArea || '_',
        attempt: attempts,
        errorMessage: errorMessage.slice(0, 1000),
        fileSize
      })
    } catch (e) {
      console.error('Error to report upload failure: ' + e)
    }
  }

  const handleQuestionSubmission = async (file: File, i: number) => {
    let question = questions[i]

    if (!question) {
      question = questions[i - 1]
    }

    // Persist the recording locally before uploading, so it can be recovered on failure/reload.
    await cacheStorage.insertFile(cacheKeyFor(question.id), file)

    setUploadError('')
    let lastErrorMessage = ''

    for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt++) {
      setUploadAttempt(attempt)
      setUploadState(attempt === 1 ? 'uploading' : 'retrying')
      setProgress(0)
      try {
        await api.sendQuestionSubmission(
          id,
          question.id,
          token,
          {
            name: user.name,
            email: user.email,
            allowEmail: true,
            allowShareInfo: false,
            jobTitle: user.jobTitle,
            functionalArea: user.functionalArea,
            country: user.country,
            audioOnly: keepAudioOnly
          },
          file,
          (event: { loaded: number; total: number }) => {
            const value = Math.round((event.loaded * 100) / event.total)
            setProgress(value)
            console.log('Uploading answer: ' + value + '% (attempt ' + attempt + ')')
          }
        )

        // Server confirmed the submission was saved: clear the local backup and show success.
        await cacheStorage.removeFile(cacheKeyFor(question.id))
        setRestoredFile(null)
        setUploadState('success')
        await sleep(1500)
        setNextStep(i)
        return
      } catch (error) {
        lastErrorMessage = error instanceof Error ? error.message : String(error)
        console.error('Failed to upload answer for question ' + question.id + ' (attempt ' + attempt + ')', error)
        if (attempt < UPLOAD_MAX_ATTEMPTS) {
          await sleep(UPLOAD_RETRY_DELAYS_MS[attempt - 1])
        }
      }
    }

    // All attempts failed: keep the recording cached, show a clear error and report it.
    setProgress(0)
    setUploadState('error')
    setUploadError(lastErrorMessage)
    reportUploadFailure(question.id, UPLOAD_MAX_ATTEMPTS, lastErrorMessage, file.size)
  }

  useEffect(() => {
    if (activeStep === 'final') {
      console.log('Final Invite ID: ' + invite.id)
      console.log('Final Invite Title: ' + invite.title)
      console.log('Final CTA: ' + invite.callToAction)
      console.log('Final CTA Button: ' + invite.callToActionButtonLabel)
      console.log('Final CTA Instructions: ' + invite.callToActionInstructions)

      localStorage.removeItem(`io.rakonto.questionnaire.${quiz.id}.lastQuestion`)
      localStorage.removeItem(`io.rakonto.questionnaire.${quiz.id}.guestUser.email`)
      localStorage.removeItem(`io.rakonto.questionnaire.${quiz.id}.guestUser.name`)
    }
  }, [activeStep])

  const handleQuestionSkipp = async (skipped: boolean, i: number) => {
    let question = questions[i]

    if (!question) {
      question = questions[i - 1]
    }

    await api
      .sendQuestionSkipp(token, {
        questionnaireId: question.questionnaireId,
        questionId: question.id,
        name: user.name,
        email: user.email,
        jobTitle: user.jobTitle,
        functionalArea: user.functionalArea,
        skipped: skipped
      })
      .then(value => {
        console.log(`Skipped: ${value.id}`)
        cacheStorage.removeFile(cacheKeyFor(question.id))
        setRestoredFile(null)
        setNextStep(i)
      })
  }

  const handleNext = (i: number) => async (file: File | null) => {
    if (!file) {
      if (collectionTheme) {
        simpleDialogActions.openWithStyles(
          'Skip Question',
          'Are you sure you want to skip this question?',
          collectionTheme!,
          { okText: 'Yes', showOk: true, cancelText: 'Cancel' },
          async (skipYes: boolean) => {
            if (skipYes) {
              handleQuestionSkipp(skipYes, i)
            }
          }
        )
      } else {
        simpleDialogActions.open(
          'Skip Question',
          'Are you sure you want to skip this question?',
          { okText: 'Yes', showOk: true, cancelText: 'Cancel' },
          async (skippYes: boolean) => {
            if (skippYes) {
              await handleQuestionSkipp(skippYes, i)
            }
          }
        )
      }
    } else {
      await handleQuestionSubmission(file, i)
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        display: 'flex',
        backgroundColor: collectionTheme?.questionnaireBackgroundColor
      }}
    >
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 1080, padding: { xs: 3, md: 4 } }}>
          {activeStep === 'start' && (
            <Step1
              collectionTheme={collectionTheme}
              questionnaire={quiz}
              user={user}
              invite={invite!}
              handleNext={(user: {
                name: string
                email: string
                jobTitle: string
                functionalArea: string
                country: string
                audioOnly: boolean
              }) => {
                setUser(user)
                localStorage.setItem(`io.rakonto.questionnaire.${quiz.id}.guestUser.email`, user.email)
                localStorage.setItem(`io.rakonto.questionnaire.${quiz.id}.guestUser.name`, user.name)
                setActiveStep(questions[0].id)
              }}
              token={token}
              quizInvite={quiz}
            />
          )}
          {questions.map((question, i) => {
            return (
              <div key={question.id}>
                {activeStep === question.id && (
                  <>
                    <Step2
                      collectionTheme={collectionTheme}
                      title={`Question ${i + 1} of ${questions.length}
                            - due ${format(parseJSON(question.collectionInvite.dueAt), 'd MMM')}
                            `}
                      invite={question.collectionInvite}
                      question={question.question}
                      questionObj={question}
                      progress={progress}
                      uploadState={uploadState}
                      uploadAttempt={uploadAttempt}
                      maxAttempts={UPLOAD_MAX_ATTEMPTS}
                      uploadError={uploadError}
                      restoredFile={restoredFile}
                      onDiscardRestored={() => {
                        cacheStorage.removeFile(cacheKeyFor(question.id))
                        setRestoredFile(null)
                      }}
                      handleNext={handleNext(i)}
                      setKeepAudioOnly={setKeepAudioOnly}
                      keepAudioOnly={keepAudioOnly}
                    />
                  </>
                )}
              </div>
            )
          })}
          {activeStep === 'final' && (
            <Step3 invite={invite} user={user} collectionTheme={collectionTheme} finishFailed={finishFailed} />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default Questionnaire
